import type { ShoppingItem } from '../types'
import { formatShoppingItem } from './format'

const compatibleUnit = (unit?: string): string | undefined => {
  if (!unit) return undefined
  if (unit === 'kg' || unit === 'g') return 'g'
  if (unit === 'L' || unit === 'mL' || unit === 'cl') return 'mL'
  return unit
}

// B4: a missing unit is compatible with any unit sharing the same base key — it must not
// block the merge of two lines that are really the same ingredient (e.g. "10 cl vin rouge"
// and "1 verre de vin rouge" with no unit at all).
const unitsAreCompatible = (a?: string, b?: string): boolean => {
  const unitA = compatibleUnit(a)
  const unitB = compatibleUnit(b)
  if (unitA === undefined || unitB === undefined) return true
  return unitA === unitB
}

const toBaseAmount = (amount: number | undefined, unit?: string): number | undefined => {
  if (amount === undefined) return undefined
  if (unit === 'kg') return amount * 1000
  if (unit === 'L') return amount * 1000
  if (unit === 'cl') return amount * 10
  return amount
}

const fromBaseAmount = (amount: number | undefined, unit?: string): number | undefined => {
  if (amount === undefined) return undefined
  if (unit === 'kg') return amount / 1000
  if (unit === 'L') return amount / 1000
  if (unit === 'cl') return amount / 10
  return amount
}

export const mergeShoppingItem = (items: ShoppingItem[], incoming: ShoppingItem): ShoppingItem[] => {
  const existingIndex = items.findIndex((item) => {
    if (item.key === incoming.key) return true
    if (item.key.split('|')[0] !== incoming.key.split('|')[0]) return false
    return unitsAreCompatible(item.unit, incoming.unit)
  })

  if (existingIndex < 0) {
    const unit = compatibleUnit(incoming.unit) ?? 'sans-unite'
    return [...items, { ...incoming, key: `${incoming.key.split('|')[0]}|${unit}` }]
  }

  const existing = items[existingIndex]
  const merged = [...items]
  // B5: resolve the unit from whichever side actually has one, so a quantity is never
  // discarded just because it merges into a line that was missing its unit.
  const resolvedUnit = existing.unit ?? incoming.unit
  const baseExisting = toBaseAmount(existing.amount, existing.unit)
  const baseIncoming = toBaseAmount(incoming.amount, incoming.unit)
  const baseSum =
    baseExisting === undefined && baseIncoming === undefined
      ? undefined
      : (baseExisting ?? 0) + (baseIncoming ?? 0)

  merged[existingIndex] = {
    ...existing,
    unit: resolvedUnit,
    amount: fromBaseAmount(baseSum, resolvedUnit),
    checked: existing.checked && incoming.checked,
    sources: Array.from(new Set([...existing.sources, ...incoming.sources])),
  }
  return merged
}

export const mergeShoppingItems = (items: ShoppingItem[], incoming: ShoppingItem[]): ShoppingItem[] =>
  incoming.reduce(mergeShoppingItem, items)

export const shoppingListText = (items: ShoppingItem[]): string =>
  items.map((item) => `${item.checked ? '☑' : '☐'} ${formatShoppingItem(item)}`).join('\n')
